import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useLocation } from 'react-router-dom';
import { TYPE } from '@kontentino/kontentino-constants/Pages';
import SocialLoginApi from 'app/modules/socialLogin/api';
import { ConnectSuccessMethod, NewAddedPage, RefusedProfile } from 'types/Page';
import {
  SocialLoginConfirmPortfolio,
  SocialLoginPage,
  SocialLoginRefusedPage,
  SocialLoginTokenType,
} from 'types/SocialLogin';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { modalRoutes } from 'constants/routes';
import { useModalHistory } from 'context/ModalRouter';
import { ApiClientError } from 'api/client';
import PageTypeUtils from 'app/utils/pageType';
import HeapUtils from 'app/utils/heap';
import {
  getConnectErrorCode,
  getConnectNetwork,
} from 'app/modules/socialLogin/utils/connectTracking';
import { SocialProfile } from 'components/modals/newSocialMediaProfile/selectSocialProfiles/SelectSocialProfileOption';

// Which pages start ticked: only the ones already connected (today), or every
// page that can be connected.
export type DefaultSelection = 'connected' | 'allConnectable';

// When the plan's page limit is enforced: as each page is ticked (today), or
// once, when Add is clicked.
export type LimitCheck = 'onSelect' | 'onSubmit';

export interface UseSocialProfilesSelectionParams {
  data: SocialLoginPage[];
  pageType: number;
  connectedPagesExternalIds: string[];
  onPagesCreate: (page: NewAddedPage) => void;
  onError?: (error: ApiClientError) => void;
  isSingleSelect?: boolean;
  clientBusinessId?: string | null;
  tokenType?: SocialLoginTokenType;
  userKey?: string | null;
  defaultSelection?: DefaultSelection;
  // The one page a profile row's button was clicked for: only it starts
  // ticked, whatever `defaultSelection` says.
  targetExternalId?: string | null;
  limitCheck?: LimitCheck;
  // Safe Connect: how long Add waits for social-login, in ms (the API's
  // default unless the host waits less).
  confirmTimeout?: number;
}

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const isDisabledPage = (page: SocialLoginPage) => page.connectable === false;

export const toSocialProfiles = (data: SocialLoginPage[]): SocialProfile[] =>
  data.map((profile) => ({
    id: profile.external_id,
    name: profile.name,
    logo: profile.logo
      ? {
          src: profile.logo,
        }
      : undefined,
    type: PageTypeUtils.getNumberFromName(profile.type),
    businessId: profile.business_id,
    businessName: profile.business_name,
    relation: profile.relation,
    ownerBusinessName: profile.owner_business_name,
    isDisabled: isDisabledPage(profile),
    disabledReason:
      isDisabledPage(profile) && profile.reason
        ? {
            key: profile.reason,
            businessName: profile.reason_business_name ?? null,
          }
        : undefined,
  }));

const uniqueByExternalId = (data: SocialLoginPage[]) =>
  Array.from(new Map(data.map((page) => [page.external_id, page])).values());

// How the confirm connected the pages, for the success view's line.
const connectMethodOf = (
  pageType: number,
  tokenType?: SocialLoginTokenType,
): ConnectSuccessMethod => {
  if (tokenType === 'system_user_obo') {
    return 'safe_connect';
  }

  return pageType === TYPE.FACEBOOK || pageType === TYPE.INSTAGRAM
    ? 'page_admin'
    : 'other';
};

type ConfirmResult = { ids: number[]; refused: SocialLoginRefusedPage[] };

// Safe Connect connects each page through its route portfolio, which is the
// row's `business_id`; the whole selection goes out in one request, grouped
// by it.
const groupByRoutePortfolio = (
  pages: SocialLoginPage[],
): SocialLoginConfirmPortfolio[] => {
  const groups = new Map<string, SocialLoginPage[]>();

  pages.forEach((page) => {
    const clientBusinessId = page.business_id ?? '';
    groups.set(clientBusinessId, [
      ...(groups.get(clientBusinessId) ?? []),
      page,
    ]);
  });

  return Array.from(groups.entries()).map(([clientBusinessId, pages]) => ({
    clientBusinessId,
    pages,
  }));
};

const isRefusedPage = (
  page: SocialLoginPage,
  refused: SocialLoginRefusedPage[],
) =>
  refused.some(
    (item) => item.external_id === page.external_id && item.type === page.type,
  );

const toRefusedProfiles = (
  refused: SocialLoginRefusedPage[],
  sentPages: SocialLoginPage[],
): RefusedProfile[] =>
  refused.map((item) => ({
    externalId: item.external_id,
    type: item.type,
    name:
      sentPages.find(
        (page) =>
          page.external_id === item.external_id && page.type === item.type,
      )?.name ?? item.external_id,
    outcome: item.outcome,
  }));

const useSocialProfilesSelection = ({
  data: rawData,
  pageType,
  connectedPagesExternalIds,
  onPagesCreate,
  onError,
  isSingleSelect,
  clientBusinessId,
  tokenType,
  userKey,
  defaultSelection = 'connected',
  targetExternalId,
  limitCheck = 'onSelect',
  confirmTimeout,
}: UseSocialProfilesSelectionParams) => {
  const data = useMemo(() => uniqueByExternalId(rawData), [rawData]);
  // Stable rows, so the list's grouping and each memoised row are not redone
  // on every selection change.
  const profiles = useMemo(() => toSocialProfiles(data), [data]);
  const isAlreadyConnectedProfile = (id: string) =>
    connectedPagesExternalIds.includes(id);
  const pageById = useMemo(
    () => new Map(data.map((page) => [page.external_id, page])),
    [data],
  );
  const disabledIds = useMemo(
    () => new Set(data.filter(isDisabledPage).map((page) => page.external_id)),
    [data],
  );

  const [selectedProfiles, setSelectedProfiles] = useState<string[]>(() =>
    data
      .filter((page) => {
        if (targetExternalId) {
          return page.external_id === targetExternalId;
        }

        return defaultSelection === 'allConnectable'
          ? !isDisabledPage(page)
          : isAlreadyConnectedProfile(page.external_id);
      })
      .map((page) => page.external_id),
  );
  const { hasReachedProfilesLimit, remainingProfiles } = useSubscriptionInfo();
  const modalHistory = useModalHistory();
  const location = useLocation();
  const network = getConnectNetwork(pageType);
  const hasTrackedListed = useRef(false);

  useEffect(() => {
    if (hasTrackedListed.current || data.length === 0) {
      return;
    }

    hasTrackedListed.current = true;
    HeapUtils.track(HeapUtils.events.CONNECT_PROFILES_LISTED, {
      network,
      profiles_available_count: data.length,
      profiles_already_connected_count: data.filter((page) =>
        connectedPagesExternalIds.includes(page.external_id),
      ).length,
    });
  }, [data, network, connectedPagesExternalIds]);

  const { mutate: mutateAddProfiles, isLoading } = useMutation<
    ConfirmResult,
    ApiClientError,
    {
      pageType: number;
      data: {
        timezone: string;
        pages: SocialLoginPage[];
      };
    }
  >(
    (variables) =>
      SocialLoginApi.addProfiles(
        variables.pageType,
        tokenType === 'system_user_obo'
          ? {
              timezone,
              portfolios: groupByRoutePortfolio(variables.data.pages),
              tokenType,
              userKey: userKey ?? null,
              confirmTimeout,
            }
          : {
              timezone,
              pages: variables.data.pages,
              clientBusinessId: clientBusinessId ?? null,
            },
      ),
    {
      onSuccess: onSuccess,
      onError: (error) => {
        HeapUtils.track(HeapUtils.events.CONNECT_PROFILES_SAVE_FAILED, {
          network,
          error_code: getConnectErrorCode(error),
        });
        onError?.(error);
      },
    },
  );

  function onSuccess(
    { ids: pageIds, refused }: ConfirmResult,
    variables: { data: { pages: SocialLoginPage[] } },
  ) {
    const hasCreated = !!pageIds && pageIds.length > 0;
    // New = sent, not connected before, and not refused; never more than the
    // pages actually created.
    const newCount = Math.min(
      variables.data.pages.filter(
        (page) =>
          !isAlreadyConnectedProfile(page.external_id) &&
          !isRefusedPage(page, refused),
      ).length,
      pageIds?.length ?? 0,
    );

    if (hasCreated) {
      HeapUtils.track(HeapUtils.events.CONNECT_PROFILES_SAVED, {
        network,
        count: newCount,
        profile_type: 'real',
      });
    }

    // A confirm that linked nothing does not get here: social-login answers it
    // with an error (422 {outcome}), which onError shows.
    if (hasCreated) {
      onPagesCreate({
        ids: pageIds,
        type: TYPE.FACEBOOK,
        newCount,
        // Listed in the success view, which replaces the picker.
        refused: toRefusedProfiles(refused, variables.data.pages),
        method: connectMethodOf(pageType, tokenType),
      });
    }
  }

  const openProfileLimitModal = () =>
    modalHistory.push({
      hash: modalRoutes.PROFILE_LIMIT_REACHED,
      search: location.search,
    });

  const handleSelect = (id: string) => {
    if (disabledIds.has(id)) {
      return;
    }

    if (
      limitCheck === 'onSelect' &&
      !selectedProfiles.includes(id) &&
      !isAlreadyConnectedProfile(id) &&
      hasReachedProfilesLimit(
        selectedProfiles.filter((id) => !isAlreadyConnectedProfile(id)).length +
          1,
      )
    ) {
      openProfileLimitModal();
      return;
    }

    setSelectedProfiles((prev) => {
      if (isSingleSelect) {
        return [id];
      }

      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
  };

  // A disabled page is never sent, not even one preselected because it is
  // already connected, so it cannot come back as refused.
  const submittableIds = selectedProfiles.filter((id) => !disabledIds.has(id));

  // The plan's limit counts only the pages this submit would add; a page that
  // is already connected never uses a slot. A negative remainder (more pages
  // connected than the plan allows) means no slots, not extra ones.
  const newSelectedCount = submittableIds.filter(
    (id) => !isAlreadyConnectedProfile(id),
  ).length;
  const isOverLimit =
    remainingProfiles !== undefined &&
    newSelectedCount > Math.max(remainingProfiles, 0);

  // Each page goes out under its route portfolio (see groupByRoutePortfolio),
  // so these are the portfolios the submit would send.
  const selectedPortfolioIds = Array.from(
    new Set(
      submittableIds.flatMap((id) => {
        const businessId = pageById.get(id)?.business_id;

        return businessId ? [businessId] : [];
      }),
    ),
  );

  const handleSubmit = () => {
    if (limitCheck === 'onSubmit' && isOverLimit) {
      openProfileLimitModal();
      return;
    }

    const pages = submittableIds.map((id) =>
      data.find((profile) => profile.external_id === id),
    ) as SocialLoginPage[];

    HeapUtils.track(HeapUtils.events.CONNECT_PROFILES_SUBMITTED, {
      network,
      selected_count: pages.length,
      new_selected_count: pages.filter(
        (page) => !isAlreadyConnectedProfile(page.external_id),
      ).length,
    });
    mutateAddProfiles({
      pageType,
      data: {
        timezone,
        pages,
      },
    });
  };

  const handleSelectAll = (filteredIds: (number | string)[]) => {
    const filteredExternalIds = filteredIds
      .map((id) => String(id))
      .filter((id) => !disabledIds.has(id));

    if (
      limitCheck === 'onSelect' &&
      hasReachedProfilesLimit(
        filteredExternalIds.filter((id) => !isAlreadyConnectedProfile(id))
          .length,
      )
    ) {
      openProfileLimitModal();
      return;
    }
    setSelectedProfiles((prev) =>
      Array.from(new Set([...prev, ...filteredExternalIds])),
    );
  };

  const handleSelectNone = () => setSelectedProfiles([]);

  // One portfolio's pages: only the connectable ones are touched, so a
  // disabled page keeps whatever state it has, and so does every other group.
  const handleSelectGroup = (groupIds: string[]) => handleSelectAll(groupIds);

  const handleSelectNoneInGroup = (groupIds: string[]) => {
    const connectableIds = new Set(
      groupIds.filter((id) => !disabledIds.has(id)),
    );

    setSelectedProfiles((prev) => prev.filter((id) => !connectableIds.has(id)));
  };

  return {
    profiles,
    selectedProfiles,
    canSubmit: submittableIds.length > 0,
    // The pages a submit would send: already-connected ones count, disabled
    // ones never do.
    selectedCount: submittableIds.length,
    isSubmitting: isLoading,
    newSelectedCount,
    remainingProfiles,
    isOverLimit,
    selectedPortfolioIds,
    handleSelect,
    handleSelectAll,
    handleSelectNone,
    handleSelectGroup,
    handleSelectNoneInGroup,
    handleSubmit,
  };
};

export default useSocialProfilesSelection;
